/**
 * Shared client contact schema — used by forms across modules
 * (Sales Pipeline, Account Management, etc.). Keep domain-agnostic.
 *
 * School / Organization store catalog IDs; `school` / `organization` hold
 * display labels for deal contact summaries.
 */
import { z } from "zod";

import { optionalPhoneSchema } from "@/lib/form-validators";

/**
 * Required-ness policy:
 *   - contactName, email, schoolId, organizationId: required
 *   - phone, address: optional
 */
export const clientInfoSchema = z.object({
  contactName: z.string().trim().min(2, "Client name is required"),
  phone: optionalPhoneSchema,
  email: z.string().trim().email("Enter a valid email"),
  schoolId: z.string().trim().min(1, "School is required"),
  school: z.string().trim().optional().or(z.literal("")),
  organizationId: z.string().trim().min(1, "Organization is required"),
  organization: z.string().trim().optional().or(z.literal("")),
  address: z
    .string()
    .trim()
    .max(255, "Address too long")
    .optional()
    .or(z.literal("")),
});

export type ClientInfoValues = z.infer<typeof clientInfoSchema>;

/** Read-only contact shape for summary panels (name, not contactName). */
export type ClientContactInfo = {
  name: string;
  phone: string;
  email: string;
  school: string;
  organization: string;
  address: string;
};
